import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Plus, Edit2, Trash2, Eye, EyeOff, Calendar, Users, 
  Newspaper, Handshake, Image, UserCog, MessageSquare, Check, X 
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger 
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = ({ section = 'dashboard' }) => {
  const [activeSection, setActiveSection] = useState(section);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [stats, setStats] = useState({});

  useEffect(() => {
    setActiveSection(section);
  }, [section]);

  useEffect(() => {
    if (activeSection === 'dashboard') {
      fetchStats();
    } else {
      fetchData();
    }
  }, [activeSection]);

  const fetchStats = async () => {
    try {
      const [matches, news, players, sponsors, gallery, messages] = await Promise.all([
        axios.get(`${API}/matches`),
        axios.get(`${API}/news?limit=100&published_only=false`),
        axios.get(`${API}/players`),
        axios.get(`${API}/sponsors?active_only=false`),
        axios.get(`${API}/gallery`),
        axios.get(`${API}/contacts`),
      ]);
      setStats({
        matches: matches.data.length,
        news: news.data.length,
        players: players.data.length,
        sponsors: sponsors.data.length,
        gallery: gallery.data.length,
        messages: messages.data.filter(m => !m.read).length,
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      let endpoint = '';
      switch (activeSection) {
        case 'matches': endpoint = '/matches'; break;
        case 'news': endpoint = '/news?limit=100&published_only=false'; break;
        case 'players': endpoint = '/players'; break;
        case 'sponsors': endpoint = '/sponsors?active_only=false'; break;
        case 'gallery': endpoint = '/gallery'; break;
        case 'direction': endpoint = '/direction'; break;
        case 'messages': endpoint = '/contacts'; break;
        default: return;
      }
      const response = await axios.get(`${API}${endpoint}`);
      setData(response.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem a certeza que deseja eliminar?')) return;
    
    try {
      let endpoint = '';
      switch (activeSection) {
        case 'matches': endpoint = `/matches/${id}`; break;
        case 'news': endpoint = `/news/${id}`; break;
        case 'players': endpoint = `/players/${id}`; break;
        case 'sponsors': endpoint = `/sponsors/${id}`; break;
        case 'gallery': endpoint = `/gallery/${id}`; break;
        case 'direction': endpoint = `/direction/${id}`; break;
        case 'messages': endpoint = `/contacts/${id}`; break;
        default: return;
      }
      await axios.delete(`${API}${endpoint}`);
      toast.success('Item eliminado com sucesso');
      fetchData();
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error('Erro ao eliminar');
    }
  };

  const handleMarkRead = async (id) => {
    try {
      await axios.put(`${API}/contacts/${id}/read`);
      toast.success('Mensagem marcada como lida');
      fetchData();
    } catch (error) {
      toast.error('Erro ao marcar como lida');
    }
  };

  const renderDashboard = () => (
    <div data-testid="admin-dashboard-main">
      <h1 className="font-heading text-3xl font-black mb-8">DASHBOARD</h1>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {[
          { label: 'Jogos', value: stats.matches || 0, icon: Calendar, section: 'matches' },
          { label: 'Notícias', value: stats.news || 0, icon: Newspaper, section: 'news' },
          { label: 'Jogadores', value: stats.players || 0, icon: Users, section: 'players' },
          { label: 'Patrocinadores', value: stats.sponsors || 0, icon: Handshake, section: 'sponsors' },
          { label: 'Galeria', value: stats.gallery || 0, icon: Image, section: 'gallery' },
          { label: 'Mensagens', value: stats.messages || 0, icon: MessageSquare, section: 'messages', highlight: true },
        ].map((stat, index) => (
          <div 
            key={index} 
            className={`border p-6 cursor-pointer hover:border-white/30 transition-colors ${
              stat.highlight && stat.value > 0 ? 'border-green-500/50 bg-green-500/5' : 'border-white/10'
            }`}
            onClick={() => window.location.href = `/admin/${stat.section}`}
          >
            <stat.icon size={24} className="mb-3 text-gray-400" />
            <p className="font-heading text-3xl font-black">{stat.value}</p>
            <p className="text-gray-400 text-xs uppercase tracking-widest mt-1">{stat.label}</p>
          </div>
        ))}
      </div>

      <div className="mt-12">
        <h2 className="font-heading text-xl font-bold mb-6">AÇÕES RÁPIDAS</h2>
        <div className="grid md:grid-cols-4 gap-4">
          <Button 
            className="justify-start gap-3 h-auto py-4 bg-white text-black hover:bg-gray-200 rounded-none"
            onClick={() => window.location.href = '/admin/jogos'}
          >
            <Calendar size={18} /> Adicionar Jogo
          </Button>
          <Button 
            className="justify-start gap-3 h-auto py-4 bg-white text-black hover:bg-gray-200 rounded-none"
            onClick={() => window.location.href = '/admin/noticias'}
          >
            <Newspaper size={18} /> Nova Notícia
          </Button>
          <Button 
            className="justify-start gap-3 h-auto py-4 bg-white text-black hover:bg-gray-200 rounded-none"
            onClick={() => window.location.href = '/admin/plantel'}
          >
            <Users size={18} /> Adicionar Jogador
          </Button>
          <Button 
            className="justify-start gap-3 h-auto py-4 bg-white text-black hover:bg-gray-200 rounded-none"
            onClick={() => window.location.href = '/admin/galeria'}
          >
            <Image size={18} /> Adicionar Foto
          </Button>
        </div>
      </div>
    </div>
  );

  const renderTable = () => {
    const configs = {
      matches: {
        title: 'JOGOS',
        columns: ['Data', 'Hora', 'Adversário', 'Local', 'Resultado', 'Ações'],
        form: MatchForm,
      },
      news: {
        title: 'NOTÍCIAS',
        columns: ['Título', 'Categoria', 'Data', 'Estado', 'Ações'],
        form: NewsForm,
      },
      players: {
        title: 'PLANTEL',
        columns: ['Nome', 'Número', 'Posição', 'Equipa', 'Ações'],
        form: PlayerForm,
      },
      sponsors: {
        title: 'PATROCINADORES',
        columns: ['Nome', 'Nível', 'Website', 'Estado', 'Ações'],
        form: SponsorForm,
      },
      gallery: {
        title: 'GALERIA',
        columns: ['Imagem', 'Título', 'Álbum', 'Época', 'Ações'],
        form: GalleryForm,
      },
      direction: {
        title: 'DIREÇÃO',
        columns: ['Nome', 'Cargo', 'Ordem', 'Ações'],
        form: DirectionForm,
      },
      messages: {
        title: 'MENSAGENS',
        columns: ['Nome', 'Email', 'Assunto', 'Data', 'Estado', 'Ações'],
        form: null,
      },
    };

    const config = configs[activeSection];
    if (!config) return null;

    const FormComponent = config.form;

    return (
      <div data-testid={`admin-${activeSection}`}>
        <div className="flex items-center justify-between mb-8">
          <h1 className="font-heading text-3xl font-black">{config.title}</h1>
          {FormComponent && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button 
                  className="gap-2 bg-white text-black hover:bg-gray-200 rounded-none"
                  onClick={() => setEditingItem(null)}
                  data-testid="add-new-btn"
                >
                  <Plus size={18} /> ADICIONAR
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#0A0A0A] border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-heading text-xl">
                    {editingItem ? 'EDITAR' : 'ADICIONAR'} {config.title.slice(0, -1)}
                  </DialogTitle>
                </DialogHeader>
                <FormComponent
                  item={editingItem}
                  onSuccess={() => {
                    setDialogOpen(false);
                    fetchData();
                  }}
                />
              </DialogContent>
            </Dialog>
          )}
        </div>

        {loading ? (
          <div className="space-y-2">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-16 bg-white/5 skeleton" />
            ))}
          </div>
        ) : data.length > 0 ? (
          <div className="border border-white/10 overflow-hidden">
            <table className="admin-table">
              <thead>
                <tr className="bg-white/5">
                  {config.columns.map((col, i) => (
                    <th key={i}>{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.map((item) => renderRow(item, config))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-16 border border-white/10">
            <p className="text-gray-500">Nenhum item encontrado</p>
          </div>
        )}
      </div>
    );
  };

  const renderRow = (item, config) => {
    switch (activeSection) {
      case 'matches':
        return (
          <tr key={item.id}>
            <td className="font-mono">{item.date}</td>
            <td className="font-mono">{item.time}</td>
            <td>{item.opponent}</td>
            <td className="text-gray-400">{item.location}</td>
            <td>
              {item.is_completed ? (
                <span className="font-mono font-bold">{item.home_score} - {item.away_score}</span>
              ) : (
                <span className="text-gray-500">-</span>
              )}
            </td>
            <td>
              <div className="flex gap-2">
                <Button 
                  size="sm" 
                  variant="ghost"
                  onClick={() => { setEditingItem(item); setDialogOpen(true); }}
                >
                  <Edit2 size={14} />
                </Button>
                <Button 
                  size="sm" 
                  variant="ghost" 
                  className="text-red-500"
                  onClick={() => handleDelete(item.id)}
                >
                  <Trash2 size={14} />
                </Button>
              </div>
            </td>
          </tr>
        );
      case 'news':
        return (
          <tr key={item.id}>
            <td className="max-w-xs truncate">{item.title}</td>
            <td className="text-gray-400 uppercase text-xs">{item.category}</td>
            <td className="font-mono text-gray-400">{new Date(item.created_at).toLocaleDateString('pt-PT')}</td>
            <td>
              {item.published ? (
                <span className="text-green-500 text-xs uppercase">Publicado</span>
              ) : (
                <span className="text-gray-500 text-xs uppercase">Rascunho</span>
              )}
            </td>
            <td>
              <div className="flex gap-2">
                <Button size="sm" variant="ghost" onClick={() => { setEditingItem(item); setDialogOpen(true); }}>
                  <Edit2 size={14} />
                </Button>
                <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDelete(item.id)}>
                  <Trash2 size={14} />
                </Button>
              </div>
            </td>
          </tr>
        );
      case 'players':
        return (
          <tr key={item.id}>
            <td>{item.name}</td>
            <td className="font-mono font-bold">{item.number}</td>
            <td className="text-gray-400">{item.position}</td>
            <td className="text-gray-400 text-xs">{item.team}</td>
            <td>
              <div className="flex gap-2">
                <Button size="sm" variant="ghost" onClick={() => { setEditingItem(item); setDialogOpen(true); }}>
                  <Edit2 size={14} />
                </Button>
                <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDelete(item.id)}>
                  <Trash2 size={14} />
                </Button>
              </div>
            </td>
          </tr>
        );
      case 'sponsors':
        return (
          <tr key={item.id}>
            <td>{item.name}</td>
            <td>
              <span className={`text-xs uppercase px-2 py-1 ${
                item.tier === 'principal' ? 'bg-white text-black' : 
                item.tier === 'secundario' ? 'bg-white/20' : 'bg-white/10 text-gray-400'
              }`}>
                {item.tier}
              </span>
            </td>
            <td className="text-gray-400 text-sm truncate max-w-xs">{item.website || '-'}</td>
            <td>
              {item.active ? (
                <span className="text-green-500 text-xs uppercase">Ativo</span>
              ) : (
                <span className="text-gray-500 text-xs uppercase">Inativo</span>
              )}
            </td>
            <td>
              <div className="flex gap-2">
                <Button size="sm" variant="ghost" onClick={() => { setEditingItem(item); setDialogOpen(true); }}>
                  <Edit2 size={14} />
                </Button>
                <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDelete(item.id)}>
                  <Trash2 size={14} />
                </Button>
              </div>
            </td>
          </tr>
        );
      case 'gallery':
        return (
          <tr key={item.id}>
            <td>
              <img src={item.image_url} alt="" className="w-16 h-12 object-cover" />
            </td>
            <td className="max-w-xs truncate">{item.title}</td>
            <td className="text-gray-400">{item.album}</td>
            <td className="text-gray-400 font-mono">{item.season}</td>
            <td>
              <div className="flex gap-2">
                <Button size="sm" variant="ghost" onClick={() => { setEditingItem(item); setDialogOpen(true); }}>
                  <Edit2 size={14} />
                </Button>
                <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDelete(item.id)}>
                  <Trash2 size={14} />
                </Button>
              </div>
            </td>
          </tr>
        );
      case 'direction':
        return (
          <tr key={item.id}>
            <td>{item.name}</td>
            <td className="text-gray-400">{item.role}</td>
            <td className="font-mono">{item.order}</td>
            <td>
              <div className="flex gap-2">
                <Button size="sm" variant="ghost" onClick={() => { setEditingItem(item); setDialogOpen(true); }}>
                  <Edit2 size={14} />
                </Button>
                <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDelete(item.id)}>
                  <Trash2 size={14} />
                </Button>
              </div>
            </td>
          </tr>
        );
      case 'messages':
        return (
          <tr key={item.id} className={!item.read ? 'bg-white/5' : ''}>
            <td className="font-medium">{item.name}</td>
            <td className="text-gray-400">{item.email}</td>
            <td className="max-w-xs truncate">{item.subject}</td>
            <td className="font-mono text-gray-400">{new Date(item.created_at).toLocaleDateString('pt-PT')}</td>
            <td>
              {item.read ? (
                <span className="text-gray-500 text-xs uppercase">Lida</span>
              ) : (
                <span className="text-green-500 text-xs uppercase">Nova</span>
              )}
            </td>
            <td>
              <div className="flex gap-2">
                {!item.read && (
                  <Button size="sm" variant="ghost" onClick={() => handleMarkRead(item.id)}>
                    <Check size={14} />
                  </Button>
                )}
                <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDelete(item.id)}>
                  <Trash2 size={14} />
                </Button>
              </div>
            </td>
          </tr>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen">
      {activeSection === 'dashboard' ? renderDashboard() : renderTable()}
    </div>
  );
};

// Form Components
const MatchForm = ({ item, onSuccess }) => {
  const [formData, setFormData] = useState(item || {
    opponent: '',
    date: '',
    time: '',
    location: '',
    home_away: 'home',
    competition: '1ª Divisão AF Braga',
    home_score: null,
    away_score: null,
    is_completed: false,
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (item) {
        await axios.put(`${API}/matches/${item.id}`, formData);
      } else {
        await axios.post(`${API}/matches`, formData);
      }
      toast.success(item ? 'Jogo atualizado!' : 'Jogo criado!');
      onSuccess();
    } catch (error) {
      toast.error('Erro ao guardar');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-xs uppercase tracking-widest text-gray-400 font-bold mb-2">Data *</label>
          <Input
            type="date"
            value={formData.date}
            onChange={(e) => setFormData({ ...formData, date: e.target.value })}
            required
            className="bg-black/50 border-white/10 rounded-none"
          />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-widest text-gray-400 font-bold mb-2">Hora *</label>
          <Input
            type="time"
            value={formData.time}
            onChange={(e) => setFormData({ ...formData, time: e.target.value })}
            required
            className="bg-black/50 border-white/10 rounded-none"
          />
        </div>
      </div>
      <div>
        <label className="block text-xs uppercase tracking-widest text-gray-400 font-bold mb-2">Adversário *</label>
        <Input
          value={formData.opponent}
          onChange={(e) => setFormData({ ...formData, opponent: e.target.value })}
          required
          className="bg-black/50 border-white/10 rounded-none"
          placeholder="Nome do adversário"
        />
      </div>
      <div>
        <label className="block text-xs uppercase tracking-widest text-gray-400 font-bold mb-2">Local *</label>
        <Input
          value={formData.location}
          onChange={(e) => setFormData({ ...formData, location: e.target.value })}
          required
          className="bg-black/50 border-white/10 rounded-none"
          placeholder="Campo/Estádio"
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-xs uppercase tracking-widest text-gray-400 font-bold mb-2">Casa/Fora</label>
          <Select value={formData.home_away} onValueChange={(v) => setFormData({ ...formData, home_away: v })}>
            <SelectTrigger className="bg-black/50 border-white/10 rounded-none">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="home">Casa</SelectItem>
              <SelectItem value="away">Fora</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="block text-xs uppercase tracking-widest text-gray-400 font-bold mb-2">Competição</label>
          <Input
            value={formData.competition}
            onChange={(e) => setFormData({ ...formData, competition: e.target.value })}
            className="bg-black/50 border-white/10 rounded-none"
          />
        </div>
      </div>
      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={formData.is_completed}
          onChange={(e) => setFormData({ ...formData, is_completed: e.target.checked })}
          id="is_completed"
        />
        <label htmlFor="is_completed" className="text-sm">Jogo terminado</label>
      </div>
      {formData.is_completed && (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs uppercase tracking-widest text-gray-400 font-bold mb-2">Golos Casa</label>
            <Input
              type="number"
              min="0"
              value={formData.home_score || ''}
              onChange={(e) => setFormData({ ...formData, home_score: parseInt(e.target.value) || 0 })}
              className="bg-black/50 border-white/10 rounded-none"
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-widest text-gray-400 font-bold mb-2">Golos Fora</label>
            <Input
              type="number"
              min="0"
              value={formData.away_score || ''}
              onChange={(e) => setFormData({ ...formData, away_score: parseInt(e.target.value) || 0 })}
              className="bg-black/50 border-white/10 rounded-none"
            />
          </div>
        </div>
      )}
      <Button type="submit" disabled={loading} className="w-full bg-white text-black hover:bg-gray-200 rounded-none">
        {loading ? 'A GUARDAR...' : 'GUARDAR'}
      </Button>
    </form>
  );
};

const NewsForm = ({ item, onSuccess }) => {
  const [formData, setFormData] = useState(item || {
    title: '',
    content: '',
    image_url: '',
    category: 'geral',
    published: true,
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (item) {
        await axios.put(`${API}/news/${item.id}`, formData);
      } else {
        await axios.post(`${API}/news`, formData);
      }
      toast.success(item ? 'Notícia atualizada!' : 'Notícia criada!');
      onSuccess();
    } catch (error) {
      toast.error('Erro ao guardar');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-xs uppercase tracking-widest text-gray-400 font-bold mb-2">Título *</label>
        <Input
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          required
          className="bg-black/50 border-white/10 rounded-none"
        />
      </div>
      <div>
        <label className="block text-xs uppercase tracking-widest text-gray-400 font-bold mb-2">Conteúdo *</label>
        <Textarea
          value={formData.content}
          onChange={(e) => setFormData({ ...formData, content: e.target.value })}
          required
          rows={6}
          className="bg-black/50 border-white/10 rounded-none"
        />
      </div>
      <div>
        <label className="block text-xs uppercase tracking-widest text-gray-400 font-bold mb-2">URL da Imagem</label>
        <Input
          value={formData.image_url}
          onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
          className="bg-black/50 border-white/10 rounded-none"
          placeholder="https://..."
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-xs uppercase tracking-widest text-gray-400 font-bold mb-2">Categoria</label>
          <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
            <SelectTrigger className="bg-black/50 border-white/10 rounded-none">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="geral">Geral</SelectItem>
              <SelectItem value="jogo">Jogo</SelectItem>
              <SelectItem value="treino">Treino</SelectItem>
              <SelectItem value="evento">Evento</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2 pt-8">
          <input
            type="checkbox"
            checked={formData.published}
            onChange={(e) => setFormData({ ...formData, published: e.target.checked })}
            id="published"
          />
          <label htmlFor="published" className="text-sm">Publicar</label>
        </div>
      </div>
      <Button type="submit" disabled={loading} className="w-full bg-white text-black hover:bg-gray-200 rounded-none">
        {loading ? 'A GUARDAR...' : 'GUARDAR'}
      </Button>
    </form>
  );
};

const PlayerForm = ({ item, onSuccess }) => {
  const [formData, setFormData] = useState(item || {
    name: '',
    number: 0,
    position: 'Médio',
    photo_url: '',
    nationality: 'Portugal',
    team: 'Seniores Masculinos',
    is_staff: false,
    staff_role: '',
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (item) {
        await axios.put(`${API}/players/${item.id}`, formData);
      } else {
        await axios.post(`${API}/players`, formData);
      }
      toast.success(item ? 'Jogador atualizado!' : 'Jogador criado!');
      onSuccess();
    } catch (error) {
      toast.error('Erro ao guardar');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-xs uppercase tracking-widest text-gray-400 font-bold mb-2">Nome *</label>
        <Input
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          required
          className="bg-black/50 border-white/10 rounded-none"
        />
      </div>
      <div className="flex items-center gap-2 mb-4">
        <input
          type="checkbox"
          checked={formData.is_staff}
          onChange={(e) => setFormData({ ...formData, is_staff: e.target.checked })}
          id="is_staff"
        />
        <label htmlFor="is_staff" className="text-sm">É membro da equipa técnica</label>
      </div>
      {formData.is_staff ? (
        <div>
          <label className="block text-xs uppercase tracking-widest text-gray-400 font-bold mb-2">Cargo *</label>
          <Input
            value={formData.staff_role}
            onChange={(e) => setFormData({ ...formData, staff_role: e.target.value })}
            className="bg-black/50 border-white/10 rounded-none"
            placeholder="Ex: Treinador Principal"
          />
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs uppercase tracking-widest text-gray-400 font-bold mb-2">Número</label>
            <Input
              type="number"
              min="0"
              value={formData.number}
              onChange={(e) => setFormData({ ...formData, number: parseInt(e.target.value) || 0 })}
              className="bg-black/50 border-white/10 rounded-none"
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-widest text-gray-400 font-bold mb-2">Posição</label>
            <Select value={formData.position} onValueChange={(v) => setFormData({ ...formData, position: v })}>
              <SelectTrigger className="bg-black/50 border-white/10 rounded-none">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Guarda-Redes">Guarda-Redes</SelectItem>
                <SelectItem value="Defesa">Defesa</SelectItem>
                <SelectItem value="Médio">Médio</SelectItem>
                <SelectItem value="Avançado">Avançado</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      )}
      <div>
        <label className="block text-xs uppercase tracking-widest text-gray-400 font-bold mb-2">Equipa</label>
        <Select value={formData.team} onValueChange={(v) => setFormData({ ...formData, team: v })}>
          <SelectTrigger className="bg-black/50 border-white/10 rounded-none">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Seniores Masculinos">Seniores Masculinos</SelectItem>
            <SelectItem value="Seniores Femininos">Seniores Femininos</SelectItem>
            <SelectItem value="Formação">Formação</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <label className="block text-xs uppercase tracking-widest text-gray-400 font-bold mb-2">URL da Foto</label>
        <Input
          value={formData.photo_url}
          onChange={(e) => setFormData({ ...formData, photo_url: e.target.value })}
          className="bg-black/50 border-white/10 rounded-none"
          placeholder="https://..."
        />
      </div>
      <Button type="submit" disabled={loading} className="w-full bg-white text-black hover:bg-gray-200 rounded-none">
        {loading ? 'A GUARDAR...' : 'GUARDAR'}
      </Button>
    </form>
  );
};

const SponsorForm = ({ item, onSuccess }) => {
  const [formData, setFormData] = useState(item || {
    name: '',
    logo_url: '',
    website: '',
    description: '',
    tier: 'apoio',
    active: true,
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (item) {
        await axios.put(`${API}/sponsors/${item.id}`, formData);
      } else {
        await axios.post(`${API}/sponsors`, formData);
      }
      toast.success(item ? 'Patrocinador atualizado!' : 'Patrocinador criado!');
      onSuccess();
    } catch (error) {
      toast.error('Erro ao guardar');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-xs uppercase tracking-widest text-gray-400 font-bold mb-2">Nome *</label>
        <Input
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          required
          className="bg-black/50 border-white/10 rounded-none"
        />
      </div>
      <div>
        <label className="block text-xs uppercase tracking-widest text-gray-400 font-bold mb-2">URL do Logo *</label>
        <Input
          value={formData.logo_url}
          onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
          required
          className="bg-black/50 border-white/10 rounded-none"
          placeholder="https://..."
        />
      </div>
      <div>
        <label className="block text-xs uppercase tracking-widest text-gray-400 font-bold mb-2">Website</label>
        <Input
          value={formData.website}
          onChange={(e) => setFormData({ ...formData, website: e.target.value })}
          className="bg-black/50 border-white/10 rounded-none"
          placeholder="https://..."
        />
      </div>
      <div>
        <label className="block text-xs uppercase tracking-widest text-gray-400 font-bold mb-2">Descrição</label>
        <Textarea
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          rows={3}
          className="bg-black/50 border-white/10 rounded-none"
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-xs uppercase tracking-widest text-gray-400 font-bold mb-2">Nível</label>
          <Select value={formData.tier} onValueChange={(v) => setFormData({ ...formData, tier: v })}>
            <SelectTrigger className="bg-black/50 border-white/10 rounded-none">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="principal">Principal</SelectItem>
              <SelectItem value="secundario">Secundário</SelectItem>
              <SelectItem value="apoio">Apoio</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2 pt-8">
          <input
            type="checkbox"
            checked={formData.active}
            onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
            id="active"
          />
          <label htmlFor="active" className="text-sm">Ativo</label>
        </div>
      </div>
      <Button type="submit" disabled={loading} className="w-full bg-white text-black hover:bg-gray-200 rounded-none">
        {loading ? 'A GUARDAR...' : 'GUARDAR'}
      </Button>
    </form>
  );
};

const GalleryForm = ({ item, onSuccess }) => {
  const [formData, setFormData] = useState(item || {
    image_url: '',
    title: '',
    description: '',
    album: 'Geral',
    season: '2024/2025',
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (item) {
        await axios.put(`${API}/gallery/${item.id}`, formData);
      } else {
        await axios.post(`${API}/gallery`, formData);
      }
      toast.success(item ? 'Imagem atualizada!' : 'Imagem adicionada!');
      onSuccess();
    } catch (error) {
      toast.error('Erro ao guardar');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-xs uppercase tracking-widest text-gray-400 font-bold mb-2">URL da Imagem *</label>
        <Input
          value={formData.image_url}
          onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
          required
          className="bg-black/50 border-white/10 rounded-none"
          placeholder="https://..."
        />
      </div>
      <div>
        <label className="block text-xs uppercase tracking-widest text-gray-400 font-bold mb-2">Título *</label>
        <Input
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          required
          className="bg-black/50 border-white/10 rounded-none"
        />
      </div>
      <div>
        <label className="block text-xs uppercase tracking-widest text-gray-400 font-bold mb-2">Descrição</label>
        <Textarea
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          rows={2}
          className="bg-black/50 border-white/10 rounded-none"
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-xs uppercase tracking-widest text-gray-400 font-bold mb-2">Álbum</label>
          <Input
            value={formData.album}
            onChange={(e) => setFormData({ ...formData, album: e.target.value })}
            className="bg-black/50 border-white/10 rounded-none"
            placeholder="Ex: Jogos, Eventos, Treinos"
          />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-widest text-gray-400 font-bold mb-2">Época</label>
          <Input
            value={formData.season}
            onChange={(e) => setFormData({ ...formData, season: e.target.value })}
            className="bg-black/50 border-white/10 rounded-none"
            placeholder="2024/2025"
          />
        </div>
      </div>
      <Button type="submit" disabled={loading} className="w-full bg-white text-black hover:bg-gray-200 rounded-none">
        {loading ? 'A GUARDAR...' : 'GUARDAR'}
      </Button>
    </form>
  );
};

const DirectionForm = ({ item, onSuccess }) => {
  const [formData, setFormData] = useState(item || {
    name: '',
    role: '',
    photo_url: '',
    order: 0,
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (item) {
        await axios.put(`${API}/direction/${item.id}`, formData);
      } else {
        await axios.post(`${API}/direction`, formData);
      }
      toast.success(item ? 'Membro atualizado!' : 'Membro adicionado!');
      onSuccess();
    } catch (error) {
      toast.error('Erro ao guardar');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-xs uppercase tracking-widest text-gray-400 font-bold mb-2">Nome *</label>
        <Input
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          required
          className="bg-black/50 border-white/10 rounded-none"
        />
      </div>
      <div>
        <label className="block text-xs uppercase tracking-widest text-gray-400 font-bold mb-2">Cargo *</label>
        <Input
          value={formData.role}
          onChange={(e) => setFormData({ ...formData, role: e.target.value })}
          required
          className="bg-black/50 border-white/10 rounded-none"
          placeholder="Ex: Presidente, Vice-Presidente"
        />
      </div>
      <div>
        <label className="block text-xs uppercase tracking-widest text-gray-400 font-bold mb-2">URL da Foto</label>
        <Input
          value={formData.photo_url}
          onChange={(e) => setFormData({ ...formData, photo_url: e.target.value })}
          className="bg-black/50 border-white/10 rounded-none"
          placeholder="https://..."
        />
      </div>
      <div>
        <label className="block text-xs uppercase tracking-widest text-gray-400 font-bold mb-2">Ordem de exibição</label>
        <Input
          type="number"
          min="0"
          value={formData.order}
          onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
          className="bg-black/50 border-white/10 rounded-none"
        />
      </div>
      <Button type="submit" disabled={loading} className="w-full bg-white text-black hover:bg-gray-200 rounded-none">
        {loading ? 'A GUARDAR...' : 'GUARDAR'}
      </Button>
    </form>
  );
};

export default AdminDashboard;
